import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Dropdown } from 'antd';
import { Link, useLocation } from 'react-router-dom';

import { api, readTotalCount } from '../api/client';
import { API_ITEMS_URL, API_ITEM_CARD_COUNTS_URL } from '../utils';
import CollectionCardList from '../components/collections/CollectionCardList';
import SubCollectionList from '../components/collections/SubCollectionList';
import LinkedFromSection from '../components/collections/LinkedFromSection';
import GroupHeader from '../components/common/GroupHeader';
import GlobalLiveNotice from '../components/collections/GlobalLiveNotice';
import { GlobalDisclaimer, ListingFacts } from '../components/globalCollections/ListingDetails';
import UsedBy from '../components/globalCollections/UsedBy';
import RetiringSources from '../components/globalCollections/RetiringSources';
import ItemAuditLine from '../components/items/ItemAuditLine';
import ItemCardsGrid from '../components/items/ItemCardsGrid';
import AddItemButton from '../components/items/AddItemButton';
import AddItemManagerModal from '../components/items/AddItemManagerModal';
import FavoriteCardButtons, { selectFavoriteCards } from '../components/items/FavoriteCardButtons';
import MultiAddButtons, { selectMultiAddForms } from '../components/items/MultiAddButtons';
import QuickAddButtons, { selectQuickAddTemplates } from '../components/items/QuickAddButtons';
import UserRef from '../components/users/UserRef';
import { KEBAB, PLUS } from '../components/icons';
import NewSubCollectionModal from '../components/collections/NewSubCollectionModal';
import AttachSubCollectionModal from '../components/collections/AttachSubCollectionModal';
import OverviewSection from '../components/overview/OverviewSection';
import { canAddSubCollection, canAttachSubCollection } from '../utils/familyActions';
import { useCardDefinitions } from '../contexts/CardDefinitionsContext';
import { useCollectionContext } from '../contexts/CollectionContext';
import { useOptionalFavorites } from '../contexts/FavoritesContext';
import useCollectionCounts from '../hooks/useCollectionCounts';
import useLinkedItems from '../hooks/useLinkedItems';
import useTileLayouts from '../hooks/useTileLayouts';
import useMe from '../hooks/useMe';
import useDraftCount from '../hooks/useDraftCount';
import useMultiAddForms from '../hooks/useMultiAddForms';
import useQuickAddTemplates from '../hooks/useQuickAddTemplates';
import { CollectionFavoriteButton } from '../components/collections/CollectionActions';
import { collectionHasNoCards, getCollectionMemberCardIds } from '../utils/collections';
import NoCardsEmptyState from '../components/collections/NoCardsEmptyState';
import NoItemsEmptyState from '../components/collections/NoItemsEmptyState';
import { parentOf, subCollectionsOf } from '../utils/collectionFamily';
import { formatCount } from '../utils/counts';
import { normalizeId } from '../utils/ids';
import { isPersonal } from '../utils/collectionKinds';
import { canAddItemsNow, canManage, roleLabel } from '../utils/permissions';
import { compareByName } from '../utils/sort';
import { getItemDisplayValue } from '../utils/items';
import RecordBadge from '../components/common/RecordBadge';
import { DataState, Skeleton, deriveDataState } from '../components/loading';

// How many of the newest items the home shows before handing over to the list.
const RECENT_LIMIT = 5;
// The "lately" window the card list's activity reads (GET /api/items/counts ?window=).
const ACTIVITY_WINDOW_DAYS = 30;

/**
 * The collection's home: the index route of `/collections/:id`, and what the
 * context bar's Home button lands on. Four sections, in the order the work
 * usually goes —
 *
 *   Add item    every shortcut the Add item modal offers, laid out in the
 *               open: the Add item primary (the exhaustive path), then the
 *               favorite cards, the quick add templates (with Manage…), and
 *               the multi add forms as launch rows — each with a pin toggle
 *               that puts it on the person's Home. Absent for a reader.
 *   Dashboard   the ONE dashboard the Overview shows (charts A7, ruled q04;
 *               OverviewSection): the manager's choice for everyone or the
 *               person's own, with where it came from, its tiles and Open
 *               dashboard. None chosen: a manager's one line, nothing for a
 *               reader.
 *   Recent      the five newest items as tiles, lookups resolved, then
 *               "View all items".
 *   Cards       the collection's cards as compact rows ordered by use —
 *               the busiest lately first — each with its activity line and
 *               a chevron opening its detail; the first six, then "Show all".
 *   About       your access, the totals, and who made it when.
 *
 * A PARENT collection (docs/plans/sub-collections.md §5.3) adds a fifth
 * section, Sub-collections, between Add item and Recent — one row each,
 * opening its home — and its Recent and Cards read the family: a tile from
 * a sub-collection wears that collection's badge, and each sub-collection's
 * own cards follow the parent's under "In {name}". A SUB-COLLECTION's About
 * gains a Part of row; its access says "via {parent}" when it comes from a
 * grant there.
 *
 * The layout (CollectionsLayout) owns the context bar for this route, so the
 * page renders body only — no second header, no breadcrumb of its own.
 */
// One About fact (Source, License, Used by…) in the home's definition list.
const ListingFact = ({ label, testId, children }) => (
	<div className="collection-home__fact" data-testid={testId}>
		<dt className="micro-label">{label}</dt>
		<dd>{children}</dd>
	</div>
);

export default function CollectionHome() {
	const { collection, resolvedCollectionId, refreshCollection } = useCollectionContext();
	const collectionId = resolvedCollectionId;
	const { cards, cardsById, getElementsForCard, loading: cardsLoading, error: cardsError, loaded: cardsLoaded, refresh: refreshCards, version: cardsVersion } = useCardDefinitions();
	// Recent draws the same tiles as the List tab, so it takes the same rows.
	const { layouts: tileLayouts } = useTileLayouts(collectionId, cardsVersion);
	// Optional: the page renders without the provider (tests, and any host
	// that mounts it outside the app shell) — the toggle simply stays away.
	const favorites = useOptionalFavorites();

	const itemsPath = collectionId ? `/collections/${collectionId}/items` : '/collections';

	// ---- The collection's own cards (a sub-collection's include the ones it
	// inherits: they are simply its cards) ----
	const memberCards = useMemo(() => {
		if (!collection) return [];
		const memberIds = getCollectionMemberCardIds(collection, cards || []);
		return (cards || [])
			.filter(card => memberIds.has(normalizeId(card?._id)))
			.sort(compareByName);
	}, [collection, cards]);

	// ---- The family: a parent's sub-collections, and each one's own cards
	// (after the parent's rows, "In Health") ----
	const subCollections = useMemo(() => subCollectionsOf(collection), [collection]);
	const subCollectionCardGroups = useMemo(() => {
		const shown = new Set(memberCards.map(card => normalizeId(card?._id)));
		return subCollections.map(sub => ({
			sub,
			cards: (cards || [])
				.filter(card => {
					const id = normalizeId(card?._id);
					return sub.cardIds.has(id) && !shown.has(id);
				})
				.sort(compareByName),
		})).filter(group => group.cards.length);
	}, [subCollections, memberCards, cards]);
	// A collection with no cards at all, once the cards have answered — a
	// failed or pending fetch knows nothing and claims nothing (KPR-322).
	const noCards = !cardsLoading && !cardsError && collectionHasNoCards(collection, cards || []);
	const parent = parentOf(collection);
	const [newSubOpen, setNewSubOpen] = useState(false);
	const [attachOpen, setAttachOpen] = useState(false);

	// The tiles' "N sub-collections" door is `…/#sub-collections`: the router
	// does not scroll to a hash, so the section does once it renders.
	const { hash } = useLocation();
	const hasSubCollections = subCollections.length > 0;
	useEffect(() => {
		if (hash !== '#sub-collections' || !hasSubCollections) return;
		document.getElementById('sub-collections')?.scrollIntoView?.({ block: 'start' });
	}, [hash, hasSubCollections]);

	// ---- Per-card activity: one aggregation (count, recentCount, the last
	// created/updated times). The rows simply go without when the API
	// predates the endpoint (404). ----
	const [cardStats, setCardStats] = useState(null);
	const [lastAddedByCollection, setLastAddedByCollection] = useState(null);
	useEffect(() => {
		if (!collectionId) {
			setCardStats(null);
			setLastAddedByCollection(null);
			return undefined;
		}
		let active = true;
		api.get(API_ITEM_CARD_COUNTS_URL, { params: { collection_id: collectionId, window: ACTIVITY_WINDOW_DAYS } })
			.then(({ data }) => {
				if (!active) return;
				const rows = Array.isArray(data) ? data : [];
				// At a parent the rows are one per card AND collection (its
				// family): a card's activity is the sum across them, and the
				// newest item per collection feeds the Sub-collections rows.
				const byCard = {};
				const lastByCollection = new Map();
				const later = (a, b) => (!a ? b : !b ? a : (new Date(b) > new Date(a) ? b : a));
				rows.forEach(row => {
					const id = normalizeId(row?.card_id);
					if (!id) return;
					const prev = byCard[id] || { count: 0, recentCount: 0, lastCreatedAt: null, lastUpdatedAt: null };
					byCard[id] = {
						count: prev.count + (Number(row?.count) || 0),
						recentCount: prev.recentCount + (Number(row?.recentCount) || 0),
						lastCreatedAt: later(prev.lastCreatedAt, row?.lastCreatedAt || null),
						lastUpdatedAt: later(prev.lastUpdatedAt, row?.lastUpdatedAt || null),
					};
					const where = normalizeId(row?.collection_id);
					if (where && row?.lastCreatedAt) lastByCollection.set(where, later(lastByCollection.get(where), row.lastCreatedAt));
				});
				setCardStats(byCard);
				setLastAddedByCollection(lastByCollection);
			})
			.catch(() => {
				if (!active) return;
				setCardStats(null);
				setLastAddedByCollection(null);
			});
		return () => { active = false; };
	}, [collectionId]);

	// ---- The newest items ----
	const [recentItems, setRecentItems] = useState([]);
	const [recentTotal, setRecentTotal] = useState(null);
	const [recentLoading, setRecentLoading] = useState(true);
	const [recentError, setRecentError] = useState(false);
	// Retry bumps this so the recent read runs again.
	const [recentKey, setRecentKey] = useState(0);
	// Another collection's tiles are never kept on screen: a switch starts over.
	useEffect(() => { setRecentItems([]); setRecentTotal(null); }, [collectionId]);
	useEffect(() => {
		if (!collectionId) {
			setRecentItems([]);
			setRecentTotal(null);
			setRecentLoading(false);
			return undefined;
		}
		let active = true;
		setRecentLoading(true);
		setRecentError(false);
		api.get(API_ITEMS_URL, {
			params: {
				collection_id: collectionId,
				limit: RECENT_LIMIT,
				skip: 0,
				sort_field: 'createdAt',
				sort_direction: 'desc',
			},
		})
			.then(({ data, headers }) => {
				if (!active) return;
				const rows = Array.isArray(data) ? data : [];
				setRecentItems(rows);
				setRecentTotal(readTotalCount(headers, rows.length));
			})
			.catch(() => {
				// The tiles already on screen stay; the failure sits above them.
				if (!active) return;
				setRecentError(true);
			})
			.finally(() => {
				if (active) setRecentLoading(false);
			});
		return () => { active = false; };
	}, [collectionId, recentKey]);

	// The items the recent tiles reference through their lookups (a feed's
	// "Who", a note's person) are fetched in one batch so a title reads as a
	// name, never a raw id — the same resolver pairing the list uses.
	const itemsById = useLinkedItems({
		items: recentItems,
		getElementsForCard,
		enabled: !cardsLoading,
		resetKey: collectionId,
	});

	// ---- Totals for the About panel. The batch endpoint the collections
	// index already uses, so this costs nothing new. ----
	const countsInput = useMemo(() => (collection ? [collection] : []), [collection]);
	const counts = useCollectionCounts(countsInput);
	const totals = counts[collectionId] || {};

	// ---- Your record: the item linked to the signed-in account here, when a
	// card carries an account link and one names them (GET …/me) ----
	const me = useMe(collectionId);
	const myRecord = me.item;
	const myCard = myRecord ? cardsById[normalizeId(myRecord.card_id)] : null;
	const myTitle = myRecord
		? ((typeof myRecord.displayValue === 'string' && myRecord.displayValue.trim())
			|| getItemDisplayValue({ card: myCard, elementDefs: getElementsForCard(myRecord.card_id), item: myRecord }))
		: '';

	// ---- Add item: the modal's shortcuts, in the open ----
	// Not in an archived collection, whoever asks: its items are read-only,
	// and the API refuses the add and every launcher pin there (the About
	// panel says it is archived).
	const canAdd = canAddItemsNow(collection);
	const quickAdds = useQuickAddTemplates(collectionId, { enabled: Boolean(collectionId) && canAdd });
	const multiAdds = useMultiAddForms(collectionId, { enabled: Boolean(collectionId) && canAdd });
	// The new items this person started here and left (the Drafts page,
	// narrowed to this collection).
	const draftCount = useDraftCount(collectionId, { enabled: Boolean(collectionId) && canAdd });
	const [managerOpen, setManagerOpen] = useState(false);

	const isFavoriteCard = useCallback(
		cardId => !!favorites && favorites.isFavoriteCard(collectionId, cardId),
		[favorites, collectionId]
	);
	const favoriteCards = useMemo(
		() => (collection ? selectFavoriteCards({ collection, cards, isFavoriteCard }) : []),
		[collection, cards, isFavoriteCard]
	);
	const quickAddEntries = useMemo(
		() => (collection ? selectQuickAddTemplates({ collection, templates: quickAdds.templates, cardsById }) : []),
		[collection, quickAdds.templates, cardsById]
	);
	const multiAddEntries = useMemo(
		() => (collection ? selectMultiAddForms({ collection, forms: multiAdds.forms, cardsById }) : []),
		[collection, multiAdds.forms, cardsById]
	);

	if (!collectionId) return null;

	const access = roleLabel(collection);
	// Intake submissions waiting for this caller's review (the API counts only
	// what the caller could triage, and leaves the key out at zero).
	const needsReview = Number(collection?.needsReview) || 0;
	// A parent holds its family's items (the list shows them all).
	const totalItems = typeof totals.familyItems === 'number' ? totals.familyItems
		: typeof totals.items === 'number' ? totals.items : recentTotal;
	// The caller's access comes (in part) from a grant on the parent.
	const accessVia = parent && normalizeId(collection?.myAccess?.inheritedFrom) === parent._id ? parent : null;
	const auditRecord = collection ? {
		createdAt: collection.createdAt,
		updatedAt: collection.updatedAt,
		owner: collection.createdBy || collection.ownedBy,
		updatedBy: collection.updatedBy,
	} : null;

	return (
		<div className="collection-home">
			<GlobalLiveNotice collection={collection} />
			<GlobalDisclaimer collection={collection} />
			<RetiringSources collection={collection} collectionId={collectionId} />
			{needsReview > 0 ? (
				// First, above the shortcuts: a stranger's submission sits outside
				// the list, counts and exports until someone here decides it, and
				// this is the only place the collection says it has any.
				<section className="collection-home__section" aria-label="Needs review">
					<GroupHeader label="Needs review" />
					<div className="collection-home__review" data-testid="collection-home-review">
						<Link
							className="collection-home__review-link"
							to={`${itemsPath}?review=pending`}
							data-action="open-review-queue"
						>
							{needsReview === 1 ? '1 submission needs review' : `${needsReview} submissions need review`}
						</Link>
						<span className="collection-home__review-note">
							Sent through intake links. They join the list once someone accepts them.
						</span>
					</div>
				</section>
			) : null}
			{canAdd ? (
				<section className="collection-home__section" aria-label="Add item">
					<GroupHeader label="Add item" />
					<div className="collection-home__add">
						<div className="collection-home__add-row collection-home__add-row--primary">
							<AddItemButton
								collectionId={collectionId}
								collection={collection}
								buttonProps={{ type: 'primary', icon: <PLUS /> }}
							/>
							{draftCount > 0 ? (
								<Link
									className="collection-home__drafts-link"
									to={`/drafts?collection=${encodeURIComponent(collectionId)}`}
									data-testid="collection-home-drafts"
								>
									{draftCount === 1 ? '1 draft waiting' : `${draftCount} drafts waiting`}
								</Link>
							) : null}
						</div>
						{favoriteCards.length ? (
							<div className="collection-home__add-row">
								<div className="micro-label collection-home__add-kicker">Favorites</div>
								<FavoriteCardButtons
									collectionId={collectionId}
									collection={collection}
									cards={cards}
									isFavoriteCard={isFavoriteCard}
									pinToHome
								/>
							</div>
						) : null}
						<div className="collection-home__add-row">
							<div className="micro-label collection-home__add-kicker">Quick add</div>
							<div className="collection-home__add-launchers">
								{quickAddEntries.length ? (
									<QuickAddButtons
										collectionId={collectionId}
										collection={collection}
										templates={quickAdds.templates}
										cardsById={cardsById}
										pinToHome
									/>
								) : (
									<DataState
										className="collection-home__quick-add-state"
										state={deriveDataState({
											loading: quickAdds.loading,
											error: quickAdds.loadError,
											hasData: false,
											isEmpty: true,
										})}
										skeleton={<Skeleton variant="line" />}
										empty={(
											<span className="launch-row launch-row--empty">
												No quick adds yet — save one from an item form, or manage them here.
											</span>
										)}
										error={quickAdds.loadError ? 'Check your connection and try again.' : null}
										onRetry={quickAdds.reload}
										label="quick adds"
									/>
								)}
								<Button
									type="text"
									size="small"
									className="collection-home__manage"
									onClick={() => setManagerOpen(true)}
								>
									Manage…
								</Button>
							</div>
						</div>
						{multiAddEntries.length ? (
							<div className="collection-home__add-row">
								<div className="micro-label collection-home__add-kicker">Multi add</div>
								<MultiAddButtons
									collectionId={collectionId}
									collection={collection}
									forms={multiAdds.forms}
									cardsById={cardsById}
									pinToHome
								/>
							</div>
						) : null}
					</div>
					{managerOpen ? (
						<AddItemManagerModal
							open={managerOpen}
							onClose={() => setManagerOpen(false)}
							onNavigate={() => setManagerOpen(false)}
							collectionId={collectionId}
							collection={collection}
							cardsById={cardsById}
							quickAdds={quickAdds}
							multiAdds={multiAdds}
						/>
					) : null}
				</section>
			) : null}

			{/* The dashboard the Overview shows (charts A7), after Add item. */}
			<OverviewSection collection={collection} collectionId={collectionId} />

			{subCollections.length ? (
				// A parent's sub-collections, between the shortcuts and the
				// newest items: the tiles' "N sub-collections" door lands here.
				<section className="collection-home__section" aria-label="Sub-collections" id="sub-collections">
					<GroupHeader
						label="Sub-collections"
						count={subCollections.length}
						countLabel="sub-collection"
						action={canAddSubCollection(collection) || canAttachSubCollection(collection) ? (
							<span className="collection-home__section-actions">
								{canAddSubCollection(collection) ? (
									<Button type="text" size="small" className="collection-home__manage" onClick={() => setNewSubOpen(true)}>
										New sub-collection…
									</Button>
								) : null}
								{canAttachSubCollection(collection) ? (
									<Dropdown
										trigger={['click']}
										placement="bottomRight"
										menu={{ items: [{ key: 'attach', label: 'Attach an existing collection…' }], onClick: () => setAttachOpen(true) }}
									>
										<Button type="text" size="small" icon={<KEBAB />} aria-label="More sub-collection actions" />
									</Dropdown>
								) : null}
							</span>
						) : null}
					/>
					<SubCollectionList subCollections={subCollections} lastAddedById={lastAddedByCollection} />
				</section>
			) : null}
			{newSubOpen ? (
				<NewSubCollectionModal open={newSubOpen} onClose={() => setNewSubOpen(false)} parent={collection} />
			) : null}
			{attachOpen ? (
				<AttachSubCollectionModal
					open={attachOpen}
					onClose={() => setAttachOpen(false)}
					parent={collection}
					onAttached={() => refreshCollection?.()}
				/>
			) : null}

			<section className="collection-home__section" aria-label="Recent items">
				{/* The header's count is the group's own, as everywhere else —
				    the collection's total is an About fact. */}
				<GroupHeader
					label="Recent"
					count={recentItems.length}
					countLabel="item"
					action={(
						<Link className="collection-home__more" to={itemsPath}>
							View all items
						</Link>
					)}
				/>
				<DataState
					state={deriveDataState({
						loading: recentLoading,
						error: recentError,
						hasData: recentTotal !== null,
						isEmpty: recentTotal !== null && !recentItems.length,
					})}
					hasData={recentTotal !== null}
					isEmpty={recentTotal !== null && !recentItems.length}
					skeleton={<Skeleton variant="tile" count={RECENT_LIMIT} />}
					empty={noCards
						// Nothing to add until there is a card: the way in, where
						// the items would be (KPR-322).
						? <NoCardsEmptyState collection={collection} collectionId={collectionId} />
						: <NoItemsEmptyState collection={collection} collectionId={collectionId} />}
					error={recentError ? 'Check your connection and try again.' : null}
					onRetry={() => setRecentKey(n => n + 1)}
					label="recent items"
				>
					<ItemCardsGrid
						items={recentItems}
						itemsById={itemsById}
						cardsById={cardsById}
						collectionId={collectionId}
						collection={collection}
						getElementsForCard={getElementsForCard}
						tileLayouts={tileLayouts}
						loading={false}
						emptyText={null}
					/>
				</DataState>
			</section>

			<section className="collection-home__section" aria-label="Cards">
				<GroupHeader
					label="Cards"
					count={memberCards.length}
					countLabel="card"
					action={canManage(collection) ? (
						<Link className="collection-home__more" to={`/collections/${collectionId}/cards`}>
							Manage
						</Link>
					) : null}
				/>
				<DataState
					state={deriveDataState({
						loading: cardsLoading,
						error: cardsError,
						hasData: cardsLoaded || memberCards.length > 0,
					})}
					hasData={cardsLoaded || memberCards.length > 0}
					skeleton={<Skeleton variant="list-row" count={3} />}
					error={cardsError ? 'Check your connection and try again.' : null}
					onRetry={refreshCards}
					label="cards"
				>
					<CollectionCardList
						cards={memberCards}
						stats={cardStats}
						windowDays={ACTIVITY_WINDOW_DAYS}
						itemsPath={itemsPath}
						collectionId={collectionId}
						getElementsForCard={getElementsForCard}
						canManage={canManage(collection)}
						emptyMessage="No cards in this collection yet — add one in Settings."
					/>
				</DataState>
				{subCollectionCardGroups.map(({ sub, cards: subCards }) => (
					<div key={sub._id} className="collection-home__card-group" data-testid="sub-collection-cards">
						<GroupHeader
							label={`In ${sub.name}`}
							count={subCards.length}
							countLabel="card"
						/>
						<CollectionCardList
							cards={subCards}
							stats={cardStats}
							windowDays={ACTIVITY_WINDOW_DAYS}
							itemsPath={`/collections/${sub._id}/items`}
							collectionId={sub._id}
							getElementsForCard={getElementsForCard}
							canManage={canManage(collection)}
						/>
					</div>
				))}
			</section>

			{/* What this collection reads from others through collection
			    links: each source, and the door into the merged list. */}
			<LinkedFromSection collectionId={collectionId} collection={collection} />

			<section className="collection-home__section" aria-label="About">
				<GroupHeader
					label="About"
					action={(
						<CollectionFavoriteButton
							isFavorite={Boolean(favorites?.isFavoriteCollection(collectionId))}
							onToggleFavorite={favorites && !isPersonal(collection)
								? () => favorites.toggleCollectionFavorite(collectionId, collection)
								: undefined}
						/>
					)}
				/>
				<dl className="collection-home__facts">
					{access ? (
						<div className="collection-home__fact">
							<dt className="micro-label">Your access</dt>
							<dd>
								{access}
								{accessVia ? (
									<span className="collection-home__via" data-testid="access-via">
										{' · via '}
										<RecordBadge kind="collection" record={accessVia} title={accessVia.name} size="xs" to={accessVia.readable ? `/collections/${accessVia._id}/` : undefined} />
									</span>
								) : null}
							</dd>
						</div>
					) : null}
					{parent ? (
						<div className="collection-home__fact" data-testid="part-of">
							<dt className="micro-label">Part of</dt>
							<dd>
								<RecordBadge
									kind="collection"
									record={parent}
									title={parent.name}
									size="xs"
									// A parent the viewer cannot open is named, never linked.
									to={parent.readable ? `/collections/${parent._id}/` : undefined}
								/>
							</dd>
						</div>
					) : null}
					{myRecord ? (
						<div className="collection-home__fact" data-testid="your-record">
							<dt className="micro-label">Your record</dt>
							<dd>
								<RecordBadge
									kind="item"
									record={myCard}
									kicker={myCard?.label?.singular || myCard?.name || undefined}
									title={myTitle}
									size="xs"
									to={`/collections/${collectionId}/items/${normalizeId(myRecord._id)}`}
								/>
							</dd>
						</div>
					) : null}
					{isPersonal(collection) ? (
						<div className="collection-home__fact">
							<dt className="micro-label">Kind</dt>
							<dd>Personal · cannot be deleted, archived or transferred</dd>
						</div>
					) : null}
					<div className="collection-home__fact">
						<dt className="micro-label">Owner</dt>
						<dd>
							{collection?.ownedBy
								? <UserRef userId={collection.ownedBy} size={20} />
								: <span className="collection-home__unknown">Unknown</span>}
						</dd>
					</div>
					<ListingFacts collection={collection} Fact={ListingFact} />
					<UsedBy collection={collection} Fact={ListingFact} />
					<div className="collection-home__fact">
						<dt className="micro-label">Holds</dt>
						<dd>
							{[
								formatCount(totalItems, 'item'),
								formatCount(memberCards.length, 'card'),
								formatCount(subCollections.length, 'sub-collection'),
							].filter(Boolean).join(' · ') || 'Nothing yet'}
						</dd>
					</div>
					{collection?.status === 'archived' ? (
						<div className="collection-home__fact">
							<dt className="micro-label">Status</dt>
							<dd>Archived — items are read-only</dd>
						</div>
					) : null}
					{auditRecord?.createdAt ? (
						<div className="collection-home__fact">
							<dt className="micro-label">Made</dt>
							<dd><ItemAuditLine item={auditRecord} /></dd>
						</div>
					) : null}
				</dl>
			</section>
		</div>
	);
}
